// Libraries
import React from 'react'
import Marketo from '../../../global/marketo'
import Layout from '../../../layouts/layout'

// Analyst Report Page Modules
import SEOTags from '../../../global/seoTags'
import FormPageHero from '../formPageHero'
import UngatedAssetDownload from '../ungatedAssetDownload'

// Helpers
import { correctSlugPrefix, generateThankYouPageURL } from '../../../../utils/link.utils'
import { findTarget } from '../../../../utils/microcopy.utils'
import { renderRichText } from '../../../../utils/richTextHandler.utils'

// Constants
import { LocaleCodes } from '../../../../constants/localization.constants'

// Styles
import { AssetDownloadContainer, BodyContent, BodyOuter, MarketoFormContainer } from '../formPage'

const AnalystReportPage = ({
  pageContext,
  location,
  path,
  headerData,
  footerData,
  announcementData,
  browserLanguageData,
}: any) => {
  let hero: any
  let formIntro = ''

  let defaultLocale
  if (pageContext && pageContext.pageLocale) {
    defaultLocale = pageContext.pageLocale
  } else {
    defaultLocale = LocaleCodes.ENGLISH.code
  }
  let pageData = pageContext.analystReportData ? pageContext.analystReportData : ''
  const resourceNumber = pageData?.resourceCategory?.number || ''

  if (pageData) {
    let hasForm = pageData.useCase === 'No Form' ? false : true
    let mcSet = pageData.microcopy?.values || []

    let downloadAsset
    if (!hasForm) {
      downloadAsset = pageData.downloadAsset?.products?.[0]
      if (downloadAsset) {
        if (!downloadAsset.title)
          downloadAsset.title = findTarget(`resource.${resourceNumber}.DownloadTITLE.text`, mcSet)
        if (!downloadAsset.text)
          downloadAsset.text = findTarget(`resource.${resourceNumber}.DownloadBODY.text`, mcSet)
      }
    }

    if (pageData.formIntro) {
      formIntro = pageData.formIntro
    } else {
      const countryCode = defaultLocale.slice(3)
      const englishRecordingRow = `resource.${resourceNumber}.FormINTRO.text`
      const globalRecordingRow = `resource.${countryCode}.${resourceNumber}.FormINTRO.text`

      formIntro = findTarget(
        defaultLocale === LocaleCodes.ENGLISH.code ? englishRecordingRow : globalRecordingRow,
        mcSet
      )
    }

    if (!hasForm && !downloadAsset) return null

    if (pageData.h1Title) {
      let heroProps = {
        h1Title: pageData.h1Title ? pageData.h1Title : '',
        image: {
          imageSrc:
            pageData.mainImage &&
            pageData.mainImage.image &&
            pageData.mainImage.image.file &&
            pageData.mainImage.image.file.url
              ? pageData.mainImage.image.file.url
              : '/',
          alt: pageData.mainImage && pageData.mainImage.altText ? pageData.mainImage.altText : '',
        },
        blueFilter: pageData.blueFilter,
        heroBullet: pageData.heroBullets,
        heroSubtitle: pageData.heroSubtitle,
        heroTextCentered: !hasForm ? false : pageData.heroTextCentered,
        heroFontBlack: pageData.heroFontBlack,
        heroEyebrow: pageData.heroEyebrow,
        hasForm: hasForm,
      }

      hero = <FormPageHero {...heroProps} />
    }

    const defaultSlug = pageData.slug ? pageData.slug : '/'
    const seoData = pageData.seoMetadata ? pageData.seoMetadata : null

    let rightColumnAlignment = !hasForm ? false : pageData.formAlignToBody

    const genericTYPageURL = '/g/thank-you-analyst-report'
    let formSuccessHandler, marketoFormData, marketoJsScript

    if (pageData.marketoJsScript && pageData.marketoJsScript.marketoJsScript) {
      marketoJsScript = pageData.marketoJsScript.marketoJsScript
    }

    if (hasForm) {
      const thankYouPageUrl = generateThankYouPageURL(genericTYPageURL, pageData.thankYouPage)

      marketoFormData = pageContext.marketoFormData
      formSuccessHandler = (form: any) => {
        if (window && window.hasOwnProperty('MktoForms2')) {
          form.onSuccess(() => {
            window.location.href = correctSlugPrefix(thankYouPageUrl)
            return false
          })
        }
      }
    }
    const pageInner = (
      <React.Fragment>
        <SEOTags
          seoMetadata={seoData}
          path={path}
          slug={defaultSlug}
          locale={defaultLocale}
          // siteSection="Analyst Report"
          availableLocales={pageContext.availableLocales}
        />
        {hero}
        <BodyOuter formAlignToBody={rightColumnAlignment}>
          <div>
            <BodyContent className="AR-WP__Body">
              {pageData.body && renderRichText(pageData.body)}
              <div className="AR-WP__FinePrint">
                {pageData.finePrint && renderRichText(pageData.finePrint)}
              </div>
            </BodyContent>
            {hasForm ? (
              <MarketoFormContainer formAlignToBody={rightColumnAlignment}>
                <div className="AR_WP__FormWrapper">
                  {!rightColumnAlignment && <span id="AR-WP__Form"></span>}
                  {formIntro && <p className="FormIntro">{formIntro}</p>}
                  <Marketo
                    formData={marketoFormData && marketoFormData.marketoForm}
                    formCallbacks={formSuccessHandler}
                    marketoJsScript={marketoJsScript}
                  />
                </div>
              </MarketoFormContainer>
            ) : (
              <AssetDownloadContainer>
                <div className="AR_WP__DownloadWrapper">
                  <UngatedAssetDownload data={downloadAsset} />
                </div>
              </AssetDownloadContainer>
            )}
          </div>
        </BodyOuter>
      </React.Fragment>
    )

    return (
      <Layout.Template
        lang={defaultLocale}
        minimalHeader={true}
        minimalFooter={true}
        headerData={headerData}
        footerData={footerData}
        announcementData={announcementData}
        browserLanguageData={browserLanguageData}
        pageType="analystReport"
        slug={defaultSlug}
        availableLocales={pageContext.availableLocales}>
        <div data-cy="AnalystReport">{pageInner}</div>
      </Layout.Template>
    )
  }
  return null
}

export default AnalystReportPage
